import React, { useEffect, useState, useMemo, useContext } from "react";
import { Input } from "design-react-kit";
import Chart from "react-apexcharts";
import Repo from "../../Utility/Repo";
import iconSprite from "bootstrap-italia/dist/svg/sprite.svg";
import { TokenContext } from "../../Components/TokenContext";
import Loader from "../../Components/Loader";

function formatEuro(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${n.toLocaleString("it-IT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;
}

function formatNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("it-IT");
}

function todayIsoDate() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function parseStatisticheIncassatiDovuti(raw) {
  const entries = Object.entries(raw || {});
  let incassatiDal = null;
  let dovutiDal = null;
  let totaleEmessoDal = null;
  let numeroUtenti = null;
  let numeroEmessi = null;
  const incassatiPerAnno = [];

  for (const [nome, valore] of entries) {
    const key = (nome || "").toLowerCase();
    const amount = valore == null ? null : Number(valore);

    const matchAnno = nome.match(/^incassati anno\s+(\d{4})$/i);
    if (matchAnno) {
      incassatiPerAnno.push({
        anno: Number(matchAnno[1]),
        valore: amount || 0,
      });
      continue;
    }

    if (key.startsWith("incassati dal")) incassatiDal = amount;
    else if (key.startsWith("dovuti dal")) dovutiDal = amount;
    else if (
      key.startsWith("totale emesso dal") ||
      key.startsWith("somma dal")
    )
      totaleEmessoDal = amount;
    else if (key.includes("numero utenti")) numeroUtenti = amount;
    else if (key.includes("numero pagamenti emessi")) numeroEmessi = amount;
  }

  incassatiPerAnno.sort((a, b) => a.anno - b.anno);

  return {
    incassatiDal,
    dovutiDal,
    totaleEmessoDal,
    numeroUtenti,
    numeroEmessi,
    incassatiPerAnno,
  };
}

function Statistiche() {
  const apiToken = useContext(TokenContext);
  const [rawStats, setRawStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [periodoDal, setPeriodoDal] = useState("2025-01-01");
  const [periodoAl, setPeriodoAl] = useState(todayIsoDate());

  const stats = useMemo(
    () => parseStatisticheIncassatiDovuti(rawStats),
    [rawStats],
  );

  const loadStatistiche = (dal, al) => {
    if (!apiToken) return;
    setStatsLoading(true);
    Repo.getTotaliIncassatiDovuti(apiToken, dal, al)
      .then((response) => {
        if (response.status === 200) setRawStats(response.data || {});
      })
      .catch((error) => console.log(error))
      .finally(() => {
        setStatsLoading(false);
        setLoading(true);
      });
  };

  useEffect(() => {
    if (!apiToken) return;
    setLoading(false);
    loadStatistiche(periodoDal, periodoAl);
  }, [apiToken]);

  const pieChart = useMemo(() => {
    const inc = Number(stats.incassatiDal) || 0;
    const dov = Number(stats.dovutiDal) || 0;
    if (inc <= 0 && dov <= 0) return null;
    return {
      series: [inc, dov],
      options: {
        chart: { type: "pie", fontFamily: "inherit" },
        labels: ["Incassati", "Dovuti"],
        colors: ["#0d8050", "#d35400"],
        legend: { position: "bottom", fontSize: "13px" },
        dataLabels: {
          formatter: (val) => `${Math.round(val)}%`,
        },
        tooltip: {
          y: { formatter: (val) => formatEuro(val) },
        },
      },
    };
  }, [stats.incassatiDal, stats.dovutiDal]);

  const chartPerAnno = useMemo(() => {
    const yearFrom = Number((periodoDal || "").slice(0, 4));
    const yearTo = Number((periodoAl || "").slice(0, 4));
    let rows = stats.incassatiPerAnno || [];
    if (Number.isFinite(yearFrom) && Number.isFinite(yearTo)) {
      rows = rows.filter((r) => r.anno >= yearFrom && r.anno <= yearTo);
    }
    if (!rows.length) return null;
    return {
      series: [{ name: "Incassati", data: rows.map((r) => r.valore) }],
      options: {
        chart: { type: "bar", fontFamily: "inherit", toolbar: { show: false } },
        plotOptions: {
          bar: {
            borderRadius: 4,
            columnWidth: "50%",
            dataLabels: { position: "top" },
          },
        },
        colors: ["#1a6bb5"],
        dataLabels: {
          enabled: true,
          offsetY: -18,
          style: { fontSize: "11px", colors: ["#333"] },
          formatter: (val) =>
            Number(val).toLocaleString("it-IT", {
              maximumFractionDigits: 0,
            }),
        },
        xaxis: {
          categories: rows.map((r) => String(r.anno)),
          labels: {
            show: true,
            style: { fontSize: "13px", fontWeight: 600 },
          },
        },
        yaxis: {
          labels: {
            formatter: (val) =>
              Number(val).toLocaleString("it-IT", {
                maximumFractionDigits: 0,
              }),
          },
        },
        tooltip: {
          y: { formatter: (val) => formatEuro(val) },
        },
        legend: { show: false },
      },
    };
  }, [stats.incassatiPerAnno, periodoDal, periodoAl]);

  return (
    <>
      <div className="row align-items-center mb-3">
        <div className="col-12">
          <h1 className="mb-1">Statistiche</h1>
          <p className="mb-0 text-muted">
            Incassati, dovuti e andamento per anno nel periodo selezionato.
          </p>
        </div>
      </div>

      <div className="row mb-2 align-items-end">
        <div className="col-md-3 form-group mb-2">
          <Input
            id="periodoDal"
            type="date"
            label="Dal"
            value={periodoDal}
            onChange={(e) => setPeriodoDal(e.target.value)}
          />
        </div>
        <div className="col-md-3 form-group mb-2">
          <Input
            id="periodoAl"
            type="date"
            label="Al"
            value={periodoAl}
            onChange={(e) => setPeriodoAl(e.target.value)}
          />
        </div>
        <div className="col-md-3 form-group mb-2">
          <button
            type="button"
            className="btn btn-outline-primary"
            disabled={statsLoading || !periodoDal || !periodoAl}
            onClick={() => loadStatistiche(periodoDal, periodoAl)}
          >
            {statsLoading ? "Aggiornamento..." : "Aggiorna statistiche"}
          </button>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-3 mb-2">
          <div className="shadow-sm p-3 bg-white rounded h-100">
            <div className="text-muted small">Utenti sul sistema</div>
            <div className="h3 mb-0">{formatNumber(stats.numeroUtenti)}</div>
          </div>
        </div>
        <div className="col-md-3 mb-2">
          <div className="shadow-sm p-3 bg-white rounded h-100">
            <div className="text-muted small">Pagamenti emessi</div>
            <div className="h3 mb-0">{formatNumber(stats.numeroEmessi)}</div>
          </div>
        </div>
        <div className="col-md-3 mb-2">
          <div className="shadow-sm p-3 bg-white rounded h-100">
            <div className="text-muted small">Incassati</div>
            <div className="h4 mb-0">{formatEuro(stats.incassatiDal)}</div>
          </div>
        </div>
        <div className="col-md-3 mb-2">
          <div className="shadow-sm p-3 bg-white rounded h-100">
            <div className="text-muted small">Dovuti</div>
            <div className="h4 mb-0">{formatEuro(stats.dovutiDal)}</div>
          </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-md-5 mb-3">
          <div className="callout h-100">
            <div className="callout-inner">
              <div className="callout-title">
                <svg className="icon">
                  <use href={`${iconSprite}#it-info-circle`} />
                </svg>
                <span className="text">Incassati / Dovuti</span>
              </div>
              <ul className="list-unstyled mb-2">
                <li className="d-flex justify-content-between border-bottom py-1">
                  <span>Incassati</span>
                  <strong>{formatEuro(stats.incassatiDal)}</strong>
                </li>
                <li className="d-flex justify-content-between border-bottom py-1">
                  <span>Dovuti</span>
                  <strong>{formatEuro(stats.dovutiDal)}</strong>
                </li>
                <li className="d-flex justify-content-between border-bottom py-1">
                  <span>Totale emesso</span>
                  <strong>{formatEuro(stats.totaleEmessoDal)}</strong>
                </li>
              </ul>
              {pieChart ? (
                <Chart
                  type="pie"
                  height={280}
                  series={pieChart.series}
                  options={pieChart.options}
                />
              ) : (
                <div className="alert alert-warning mb-0">
                  Nessun dato nel periodo selezionato
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-7 mb-3">
          <div className="callout h-100">
            <div className="callout-inner">
              <div className="callout-title">
                <svg className="icon">
                  <use href={`${iconSprite}#it-chart-line`} />
                </svg>
                <span className="text">Incassati per anno</span>
              </div>
              {chartPerAnno ? (
                <Chart
                  type="bar"
                  height={320}
                  series={chartPerAnno.series}
                  options={chartPerAnno.options}
                />
              ) : (
                <div className="alert alert-warning mb-0">
                  Nessun dato annuale nel periodo selezionato
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {!loading && <Loader />}
    </>
  );
}

export default Statistiche;
